import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldQuestion, ArrowLeft, Save, CheckCircle2, Loader2, Lock, AlertCircle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useTranslation } from '../i18nContext';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

/**
 * ProfileSecurityPage — let the logged-in user configure their 2 security
 * questions (chosen from the 10-question catalog). Requires re-typing the
 * current password to lower the risk of an attacker with a hijacked session
 * silently weakening recovery.
 */
export default function ProfileSecurityPage() {
  const t = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [questionIds, setQuestionIds] = useState([]);
  const [pickIds, setPickIds] = useState([0, 0]);   // selected question IDs
  const [answers, setAnswers] = useState(['', '']);
  const [currentPwd, setCurrentPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: cat }, { data: mine }] = await Promise.all([
          api.get('/auth/security-questions-catalog'),
          api.get('/me/security-questions'),
        ]);
        setCatalog(cat || []);
        setConfigured(!!mine.configured);
        setQuestionIds(mine.question_ids || []);
        // Pre-fill picker with existing IDs if configured.
        if (mine.question_ids && mine.question_ids.length >= 2) {
          setPickIds([mine.question_ids[0], mine.question_ids[1]]);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  function setPick(idx, qid) {
    const next = [...pickIds]; next[idx] = qid; setPickIds(next);
  }

  async function submit(e) {
    e.preventDefault();
    if (!pickIds[0] || !pickIds[1]) { setError('Elige dos preguntas distintas.'); return; }
    if (pickIds[0] === pickIds[1]) { setError('Las dos preguntas deben ser distintas.'); return; }
    if (!answers[0].trim() || !answers[1].trim()) { setError('Responde ambas preguntas.'); return; }
    if (!currentPwd) { setError('Confirma tu contraseña actual.'); return; }
    setError(''); setSaving(true);
    try {
      await api.post('/me/security-questions', {
        current_password: currentPwd,
        items: [
          { question_id: pickIds[0], answer: answers[0] },
          { question_id: pickIds[1], answer: answers[1] },
        ],
      });
      setSuccess(true);
      setConfigured(true);
      setQuestionIds(pickIds);
      setAnswers(['', '']);
      setCurrentPwd('');
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error');
    }
    setSaving(false);
  }

  const isEs = t?.locale !== 'en';
  const questionLabel = (q) => (isEs ? q.es : q.en);

  return (
    <div className="bg-gray-50 min-h-screen pt-24 pb-12" data-testid="profile-security-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to={`/${t.locale}/perfil`} className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 mb-4">
          <ArrowLeft size={16} />Volver al perfil
        </Link>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-cmic-50 text-cmic-600 flex items-center justify-center flex-shrink-0">
              <ShieldQuestion size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-charcoal-800">Preguntas de seguridad</h1>
              <p className="text-sm text-charcoal-500 mt-1">
                Configura 2 preguntas secretas para poder recuperar tu contraseña si pierdes acceso al correo. Tus respuestas se guardan cifradas y no son visibles ni siquiera para los administradores.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-cmic-500" size={26} /></div>
          ) : (
            <>
              {configured && (
                <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="security-current-status">
                  <CheckCircle2 size={16} />
                  Ya tienes preguntas configuradas. Puedes actualizarlas a continuación si lo deseas.
                </div>
              )}

              <form onSubmit={submit} className="space-y-6" data-testid="security-form">
                {[0, 1].map((idx) => (
                  <div key={idx} className="space-y-2 border border-gray-100 rounded-xl p-4">
                    <label className="block text-xs font-semibold text-charcoal-700 uppercase tracking-wider">
                      Pregunta {idx + 1}
                    </label>
                    <Select
                      value={pickIds[idx] ? String(pickIds[idx]) : undefined}
                      onValueChange={(v) => setPick(idx, Number(v))}
                    >
                      <SelectTrigger
                        data-testid={`security-pick-${idx}`}
                        className="w-full h-auto rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                      >
                        <SelectValue placeholder="— Elige una pregunta —" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalog.map((q) => (
                          <SelectItem key={q.id} value={String(q.id)} disabled={pickIds[1 - idx] === q.id} data-testid={`security-pick-${idx}-option-${q.id}`}>
                            {questionLabel(q)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input
                      type="text"
                      value={answers[idx]}
                      onChange={(e) => {
                        const next = [...answers]; next[idx] = e.target.value; setAnswers(next);
                      }}
                      data-testid={`security-answer-${idx}`}
                      placeholder="Tu respuesta"
                      autoComplete="off"
                      required
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                    />
                    <p className="text-[11px] text-charcoal-400">
                      Las respuestas son insensibles a mayúsculas y acentos.
                    </p>
                  </div>
                ))}

                <div className="border-t border-gray-100 pt-5">
                  <label className="block text-xs font-medium text-charcoal-700 mb-1.5 flex items-center gap-1">
                    <Lock size={11} /> Confirma tu contraseña actual
                  </label>
                  <input
                    type="password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    data-testid="security-current-pwd"
                    required
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2" data-testid="security-error">
                    <AlertCircle size={14} />{error}
                  </div>
                )}
                {success && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="security-success">
                    <CheckCircle2 size={14} />Preguntas guardadas correctamente.
                  </div>
                )}

                <button type="submit" disabled={saving} data-testid="security-submit"
                  className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-2.5 transition-all duration-200 disabled:opacity-50">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} />Guardar preguntas</>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
